'use client'

import { useEffect, useState } from 'react'
import { Figure } from '@systhemaui/react'

interface CountdownTimerProps {
  targetDate: string
  /** When true, renders without Figure wrapper (for use inside hero sections) */
  bare?: boolean
}

interface TimeLeft {
  days: number
  hours: number
  minutes: number
  seconds: number
}

function calculateTimeLeft(targetDate: string): TimeLeft {
  const target = new Date(targetDate)
  const diff = Math.max(0, target.getTime() - Date.now())

  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  }
}

const UNITS = ['days', 'hours', 'minutes', 'seconds'] as const

const unitLabels: Record<keyof TimeLeft, string> = {
  days: 'Nap',
  hours: 'Óra',
  minutes: 'Perc',
  seconds: 'Mp',
}

function TimeUnit({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col items-center" style={{ minWidth: '18vw' }}>
      <span
        className="leading-none font-bold tabular-nums"
        style={{ fontSize: 'clamp(2.5rem, 12vw, 8rem)', color: '#D50000' }}
      >
        {value}
      </span>
      <span
        className="color-label mt-1 tracking-widest uppercase"
        style={{ fontSize: 'clamp(0.6rem, 2vw, 0.8rem)' }}
      >
        {label}
      </span>
    </div>
  )
}

function Separator() {
  return (
    <span
      className="self-start leading-none font-bold opacity-30"
      style={{ fontSize: 'clamp(2rem, 10vw, 6.5rem)', color: '#D50000' }}
    >
      :
    </span>
  )
}

function CountdownDisplay({ values }: { values: string[] }) {
  return (
    <div
      className="flex w-full items-start justify-center"
      style={{ gap: 'clamp(0.25rem, 1vw, 1rem)' }}
    >
      <TimeUnit value={values[0]} label={unitLabels.days} />
      <Separator />
      <TimeUnit value={values[1]} label={unitLabels.hours} />
      <Separator />
      <TimeUnit value={values[2]} label={unitLabels.minutes} />
      <Separator />
      <TimeUnit value={values[3]} label={unitLabels.seconds} />
    </div>
  )
}

export default function CountdownTimer({ targetDate, bare }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | undefined>(undefined)

  useEffect(() => {
    const update = () => {
      const tl = calculateTimeLeft(targetDate)
      setTimeLeft(tl)
      return tl.days === 0 && tl.hours === 0 && tl.minutes === 0 && tl.seconds === 0
    }
    const expired = update()
    if (expired) return

    const timer = setInterval(() => {
      if (update()) clearInterval(timer)
    }, 1000)

    return () => clearInterval(timer)
  }, [targetDate])

  const values = timeLeft
    ? UNITS.map((u) => String(timeLeft[u]).padStart(2, '0'))
    : UNITS.map(() => '--')

  if (bare) {
    return <CountdownDisplay values={values} />
  }

  return (
    <Figure width="container">
      <CountdownDisplay values={values} />
    </Figure>
  )
}
